import React from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { buildYearId, normalizeYearKey, sortYearKeys } from './accountImportUtils';

const SectionTable = ({ rows, columns, sectionKey, yearKey, getKey, isItemSelected, onToggleSelection }) => (
    <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
            <TableHead>
                <TableRow>
                    <TableCell padding="checkbox" />
                    {columns.map((column) => (
                        <TableCell key={column.label} align={column.align || 'left'}>
                            {column.label}
                        </TableCell>
                    ))}
                </TableRow>
            </TableHead>
            <TableBody>
                {rows.map((item) => {
                    const selectionKey = getKey(item);
                    const checked = isItemSelected(sectionKey, selectionKey, yearKey);
                    return (
                        <TableRow key={String(selectionKey)} hover>
                            <TableCell padding="checkbox">
                                <Checkbox
                                    checked={checked}
                                    onChange={() => onToggleSelection(sectionKey, selectionKey, yearKey)}
                                />
                            </TableCell>
                            {columns.map((column) => (
                                <TableCell key={column.label} align={column.align || 'left'}>
                                    {column.render(item)}
                                </TableCell>
                            ))}
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
    </Box>
);

export default function PreviewSections({
    sections,
    selection,
    sectionData,
    sectionYears,
    yearData,
    yearLoading,
    yearErrors,
    expandedYears,
    sectionLoading,
    sectionErrors,
    getSectionSelectionMetrics,
    getSectionYearSelectionMetrics,
    getYearSelectionMetrics,
    getYearCount,
    isItemSelected,
    toggleSelection,
    toggleAll,
    loadSection,
    loadSectionYears,
    loadYearItems,
    setYearExpanded,
    handleYearToggle,
    setExpandedYears,
}) {
    if (!selection) {
        return null;
    }

    const renderSection = (config) => {
        if (!config.count) {
            return null;
        }
        const sectionMetrics = config.groupByYear && Array.isArray(sectionYears[config.key])
            ? getSectionYearSelectionMetrics(config.key, config.count)
            : getSectionSelectionMetrics(config.key, config.count);
        const { allSelected, someSelected, selectedCount } = sectionMetrics;
        const isLoading = sectionLoading[config.key];
        const error = sectionErrors[config.key];
        const headerCheckbox = config.singleSelect ? (
            <Checkbox
                checked={false}
                disabled
                inputProps={{ 'aria-label': `${config.title} Einzelauswahl` }}
            />
        ) : (
            <Checkbox
                checked={allSelected}
                indeterminate={!allSelected && someSelected}
                onChange={() => toggleAll(config.key, allSelected)}
                onClick={(event) => event.stopPropagation()}
                onFocus={(event) => event.stopPropagation()}
            />
        );

        if (config.groupByYear) {
            const yearEntries = Array.isArray(sectionYears[config.key]) ? sectionYears[config.key] : [];
            const yearKeys = sortYearKeys(yearEntries.map((entry) => normalizeYearKey(entry?.year)));
            const isLoaded = Array.isArray(sectionYears[config.key]);

            return (
                <Accordion
                    defaultExpanded={false}
                    onChange={(event, expanded) => {
                        if (expanded) {
                            loadSectionYears(config.key);
                        } else {
                            setExpandedYears((prev) => ({ ...prev, [config.key]: {} }));
                        }
                    }}
                    sx={{ mt: 2 }}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                            {headerCheckbox}
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    {config.title} ({config.count})
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {selectedCount} von {config.count} ausgewaehlt
                                </Typography>
                            </Box>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                        {isLoading && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CircularProgress size={20} />
                                <Typography variant="body2" color="text.secondary">
                                    Vorschau laedt...
                                </Typography>
                            </Box>
                        )}
                        {!isLoading && error && (
                            <Alert severity="error">{error}</Alert>
                        )}
                        {!isLoading && !error && !isLoaded && (
                            <Typography variant="body2" color="text.secondary">
                                Bereich oeffnen, um die Vorschau zu laden.
                            </Typography>
                        )}
                        {!isLoading && isLoaded && (
                            <>
                                {yearKeys.length ? (
                                    <Stack spacing={1.5}>
                                        {yearKeys.map((yearKey) => {
                                            const yearId = buildYearId(config.key, yearKey);
                                            const totalCount = getYearCount(config.key, yearKey);
                                            const yearItems = yearData?.[config.key]?.[yearKey];
                                            const yearRows = Array.isArray(yearItems) ? yearItems : [];
                                            const yearItemsWithKey = Array.isArray(yearItems)
                                                ? yearRows.filter((item) => {
                                                    const value = config.getKey(item);
                                                    return value !== null && value !== undefined && value !== '';
                                                })
                                                : [];
                                            const yearSelection = getYearSelectionMetrics(
                                                config.key,
                                                yearKey,
                                                yearItemsWithKey,
                                                config.getKey,
                                                totalCount
                                            );
                                            const yearExpanded = Boolean(expandedYears?.[config.key]?.[yearKey]);
                                            const isYearLoading = yearLoading[yearId];
                                            const yearError = yearErrors[yearId];
                                            const loadedCount = yearRows.length;
                                            const hasMore = loadedCount < totalCount;

                                            return (
                                                <Accordion
                                                    key={`${config.key}-${yearKey}`}
                                                    expanded={yearExpanded}
                                                    onChange={(event, expanded) => {
                                                        setYearExpanded(config.key, yearKey, expanded);
                                                        if (expanded) {
                                                            loadYearItems(config.key, yearKey);
                                                        }
                                                    }}
                                                >
                                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Checkbox
                                                                checked={yearSelection.allSelected}
                                                                indeterminate={!yearSelection.allSelected && yearSelection.someSelected}
                                                                onChange={() => handleYearToggle(
                                                                    config.key,
                                                                    yearKey,
                                                                    totalCount,
                                                                    yearSelection.allSelected
                                                                )}
                                                                onClick={(event) => event.stopPropagation()}
                                                                onFocus={(event) => event.stopPropagation()}
                                                                inputProps={{ 'aria-label': `Jahr ${yearKey}` }}
                                                            />
                                                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                                    Jahr {yearKey} ({totalCount})
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {yearSelection.selectedCount} von {totalCount} ausgewaehlt
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    </AccordionSummary>
                                                    <AccordionDetails>
                                                        {isYearLoading && (
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <CircularProgress size={18} />
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Vorschau laedt...
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                        {!isYearLoading && yearError && (
                                                            <Alert severity="error">{yearError}</Alert>
                                                        )}
                                                        {!isYearLoading && !yearError && !Array.isArray(yearItems) && (
                                                            <Typography variant="body2" color="text.secondary">
                                                                Jahr oeffnen, um die Vorschau zu laden.
                                                            </Typography>
                                                        )}
                                                        {!isYearLoading && Array.isArray(yearItems) && (
                                                            <>
                                                                {yearItemsWithKey.length ? (
                                                                    <SectionTable
                                                                        rows={yearItemsWithKey}
                                                                        columns={config.columns}
                                                                        sectionKey={config.key}
                                                                        yearKey={yearKey}
                                                                        getKey={config.getKey}
                                                                        isItemSelected={isItemSelected}
                                                                        onToggleSelection={toggleSelection}
                                                                    />
                                                                ) : (
                                                                    <Alert severity="info">Keine Eintraege vorhanden.</Alert>
                                                                )}
                                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {loadedCount} von {totalCount} geladen
                                                                    </Typography>
                                                                    {hasMore && (
                                                                        <Button
                                                                            size="small"
                                                                            variant="outlined"
                                                                            onClick={() => loadYearItems(config.key, yearKey, { loadMore: true })}
                                                                        >
                                                                            Mehr laden
                                                                        </Button>
                                                                    )}
                                                                </Box>
                                                            </>
                                                        )}
                                                    </AccordionDetails>
                                                </Accordion>
                                            );
                                        })}
                                    </Stack>
                                ) : (
                                    <Alert severity="info">Keine Eintraege vorhanden.</Alert>
                                )}
                            </>
                        )}
                    </AccordionDetails>
                </Accordion>
            );
        }

        const items = sectionData[config.key];
        const isLoaded = Array.isArray(items);
        const rows = isLoaded ? items : [];
        const itemsWithKey = isLoaded
            ? rows.filter((item) => {
                const value = config.getKey(item);
                return value !== null && value !== undefined && value !== '';
            })
            : [];
        const loadedCount = rows.length;
        const hasMore = loadedCount < config.count;

        return (
            <Accordion
                defaultExpanded={false}
                onChange={(event, expanded) => {
                    if (expanded) {
                        loadSection(config.key);
                    }
                }}
                sx={{ mt: 2 }}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        {headerCheckbox}
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                {config.title} ({config.count})
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {selectedCount} von {config.count} ausgewaehlt
                            </Typography>
                        </Box>
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    {isLoading && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CircularProgress size={20} />
                            <Typography variant="body2" color="text.secondary">
                                Vorschau laedt...
                            </Typography>
                        </Box>
                    )}
                    {!isLoading && error && (
                        <Alert severity="error">{error}</Alert>
                    )}
                    {!isLoading && !error && !isLoaded && (
                        <Typography variant="body2" color="text.secondary">
                            Bereich oeffnen, um die Vorschau zu laden.
                        </Typography>
                    )}
                    {!isLoading && isLoaded && (
                        <>
                            {itemsWithKey.length ? (
                                <SectionTable
                                    rows={itemsWithKey}
                                    columns={config.columns}
                                    sectionKey={config.key}
                                    getKey={config.getKey}
                                    isItemSelected={isItemSelected}
                                    onToggleSelection={toggleSelection}
                                />
                            ) : (
                                <Alert severity="info">Keine Eintraege vorhanden.</Alert>
                            )}
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                    {loadedCount} von {config.count} geladen
                                </Typography>
                                {hasMore && (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => loadSection(config.key, { loadMore: true })}
                                    >
                                        Mehr laden
                                    </Button>
                                )}
                            </Box>
                        </>
                    )}
                </AccordionDetails>
            </Accordion>
        );
    };

    return (
        <>
            {sections.map((section) => (
                <React.Fragment key={section.key}>
                    {renderSection(section)}
                </React.Fragment>
            ))}
        </>
    );
}
